import { User, UserType } from 'Douleutaras/types';
import { LOCAL_STORAGE_KEY } from 'Douleutaras/constants/system';
import { AuthObjectFormat, UserTokenStorage } from 'Douleutaras/interfaces/auth';
import qs from 'qs';
import { useGetParams } from 'Douleutaras/hooks/get-params';
import { getMe } from 'Douleutaras/redux/data/users/meSlice';
import { isLoginPage } from 'Douleutaras/utilities/isLoginPage';
import { getStoredUser } from 'Douleutaras/utilities/localStorage';

import {
  clearUserAndRedirect,
  getUser,
  setJwtToken,
  setTempUser,
  setUser,
  userLogout
} from '../redux/data/auth/authSlice';

/**
 * Check a user's status
 * Dispatch appropriate actions when the user has been found in session or has JWT token
 */
export const checkUserAuthentication =
  async (storage: UserTokenStorage, { dispatch })
    : Promise<{ currentUser: User, jwtToken: string }> => {
    if (typeof window !== 'undefined') {
      injectMobileJs(storage);
    }
    const params = useGetParams();
    // Use the user's object stored in the session or initiate a new object
    const userData = storage.get(LOCAL_STORAGE_KEY.USER_CREDENTIALS) as AuthObjectFormat;
    let currentUser: User = userData ? userData.v.user : undefined;

    // Get full user with a token. Check only if we are NOT on the login page
    if (!isLoginPage() && !currentUser && params['token']) {
      const me = await dispatch(getMe());
      const myId = me.payload?.data?.id;
      if (myId) {
        // Get user but don't update the storage since it's temporary
        const tokenUser = await dispatch(getUser({ id: me.payload.data.id, updateUsageStorage: false }))
          .unwrap();
        // A token-authenticated user always enters the app as a buyer. getUser.fulfilled has
        // already stored the raw payload, so overwrite it with the buyer-typed user.
        // TODO: Refactor this after https://gitlab.com/d3454/core/douleutaras/-/work_items/5583 is done
        currentUser = { ...tokenUser, user_type: UserType.BUYER };
        await dispatch(setUser(currentUser));
        await dispatch(setTempUser(true));
      }
    } else if (currentUser?.id) {
      // User object is available. Put into the redux store for later use
      await dispatch(setUser(currentUser));
      await dispatch(setJwtToken(userData.v.token));
      await dispatch(setTempUser(false));
    }

    return { currentUser, jwtToken: userData?.v?.token || params['token'] as string };
  };

// This method is used to inject Mobile specific code to be used in webview
export const injectMobileJs = (storage: UserTokenStorage) => {

  if (window.location.search.indexOf('?') === -1) {
    return false;
  }

  const getParamsString = window.location.search.substr(1);
  const getParams = qs.parse(getParamsString);

  const mobileAutoSignIn = typeof getParams.mobileAutoSignIn !== 'undefined' && getParams.mobileAutoSignIn === 'true';

  // Check if mobileAuthSignIn get param is present and also window.mobileAutoLoginAuthData object is set
  if (mobileAutoSignIn) {
    // @ts-ignore
    if (typeof window.mobileAutoLoginAuthData !== 'undefined') {
      // @ts-ignore
      const mobileAutoLoginAuthData = window.mobileAutoLoginAuthData;
      // Use window.mobileAutoLoginAuthData object set from mobile app, and inject it into localstorage
      // tslint:disable-next-line:no-any
      storage.set(LOCAL_STORAGE_KEY.USER_CREDENTIALS, { v: mobileAutoLoginAuthData} as any);
    } else {
      storage.set(LOCAL_STORAGE_KEY.USER_CREDENTIALS, undefined);
    }
  }
};

export const logoutBrokenUser = async dispatch => {
  const currentUser = getStoredUser();

  if (currentUser) {
    await dispatch(userLogout({ id: currentUser.id }));

    return;
  }
  await clearUserAndRedirect(dispatch);
};
